import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import formatBytes from '../utils/formatBytes';

// ---------------------------------------------------------------------------

function FileRow({ file, directory, onDownload, onDelete, downloading }) {
  const filePath = `/${directory}/${file.name}`;
  const isDownloading = downloading === filePath;

  return (
    <ListItem
      secondaryAction={
        <>
          <IconButton
            edge="end"
            aria-label={`download ${file.name}`}
            onClick={() => onDownload(filePath)}
            disabled={isDownloading}
            sx={{ mr: 0.5 }}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            edge="end"
            aria-label={`delete ${file.name}`}
            onClick={() => onDelete(filePath)}
            disabled={isDownloading}
          >
            <DeleteIcon />
          </IconButton>
        </>
      }
    >
      <ListItemText
        primary={file.name}
        secondary={formatBytes(file.size)}
      />
      {isDownloading && (
        <Box sx={{ width: '100%', position: 'absolute', bottom: 0, left: 0 }}>
          <LinearProgress />
        </Box>
      )}
    </ListItem>
  );
}

FileRow.propTypes = {
  file: PropTypes.shape({ name: PropTypes.string, size: PropTypes.number }).isRequired,
  directory: PropTypes.string.isRequired,
  onDownload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  downloading: PropTypes.string,
};

// ---------------------------------------------------------------------------

function FileSection({ title, directory, files, onDownload, onDelete, downloading }) {
  if (!files || files.length === 0) return null;

  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <List dense>
          {files.map((file) => (
            <FileRow
              key={file.name}
              file={file}
              directory={directory}
              onDownload={onDownload}
              onDelete={onDelete}
              downloading={downloading}
            />
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
}

FileSection.propTypes = {
  title: PropTypes.string.isRequired,
  directory: PropTypes.string.isRequired,
  files: PropTypes.array.isRequired,
  onDownload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  downloading: PropTypes.string,
};

// ---------------------------------------------------------------------------

export default function DeviceFileBrowser({
  files,
  directory = '',
  passiveFiles = [],
  captureFiles = [],
  onDownload,
  onDelete,
  downloading = null,
}) {
  const [confirmDelete, setConfirmDelete] = useState(null);

  const handleDeleteClick = (filePath) => {
    setConfirmDelete(filePath);
  };

  const handleConfirmDelete = () => {
    if (confirmDelete) {
      onDelete(confirmDelete);
    }
    setConfirmDelete(null);
  };

  const handleCancelDelete = () => {
    setConfirmDelete(null);
  };

  const deleteDialog = (
    <Dialog open={confirmDelete !== null} onClose={handleCancelDelete}>
      <DialogTitle>Delete File</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete <strong>{confirmDelete}</strong>? This cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelDelete}>Cancel</Button>
        <Button onClick={handleConfirmDelete} color="error" variant="contained">
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );

  const emptyMessage = (
    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
      No files found on the device.
    </Typography>
  );

  // Single-directory mode: caller passes a `files` array for one `directory`
  // (the Sensor Manager Files tab, whose sidebar already selects the category).
  if (files !== undefined) {
    if (!files || files.length === 0) return emptyMessage;
    return (
      <>
        <List dense>
          {files.map((file) => (
            <FileRow
              key={file.name}
              file={file}
              directory={directory}
              onDownload={onDownload}
              onDelete={handleDeleteClick}
              downloading={downloading}
            />
          ))}
        </List>
        {deleteDialog}
      </>
    );
  }

  // Legacy two-section mode: passive + captures shown together (DeviceFilesPage).
  const totalFiles = (passiveFiles?.length || 0) + (captureFiles?.length || 0);
  if (totalFiles === 0) return emptyMessage;

  return (
    <>
      <FileSection
        title="Background Recordings"
        directory="data/passive"
        files={passiveFiles}
        onDownload={onDownload}
        onDelete={handleDeleteClick}
        downloading={downloading}
      />
      <FileSection
        title="Captures"
        directory="data/captures"
        files={captureFiles}
        onDownload={onDownload}
        onDelete={handleDeleteClick}
        downloading={downloading}
      />
      {deleteDialog}
    </>
  );
}

DeviceFileBrowser.propTypes = {
  files: PropTypes.array,
  directory: PropTypes.string,
  passiveFiles: PropTypes.array,
  captureFiles: PropTypes.array,
  onDownload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  downloading: PropTypes.string,
};

